import { Button } from '@/components/common/button'
import { LabeledValue } from '@/components/common/labeledValue'
import { ProtocolImage } from '@/components/common/protocolImage'
import { TokenImage } from '@/components/common/tokenImage'
import { ExternalLinkIcon } from '@/components/icons/externalLink'
import { CuratorInfo } from '@/components/shared/curatorInfo'
import type { Vault } from '@/types/vault'
import { getExplorerLink, getExplorerName } from '@/utils/explorer'
import { getProtocolDisplayName } from '@/utils/protocol'

interface Props {
  vault: Vault
}

export function StrategyOverview({ vault }: Props) {
  const { protocol, curator, asset, rewards } = vault
  const protocolDisplayName = getProtocolDisplayName(protocol)
  const explorerLink = getExplorerLink(vault.network.name)
  const explorerName = getExplorerName(vault.network.name)

  return (
    <div className="grid grid-cols-[repeat(auto-fit,minmax(164px,1fr))] gap-3">
      <LabeledValue label="Protocol">
        {protocol.protocolUrl ? (
          <Button
            variant="default"
            size="default"
            className="flex items-center justify-start gap-1 rounded-lg text-base underline"
            asChild
          >
            <a href={protocol.protocolUrl} target="_blank" rel="noreferrer">
              <ProtocolImage protocol={protocol} className="size-6" />
              <span>{protocolDisplayName}</span>
            </a>
          </Button>
        ) : (
          <div className="flex items-center gap-1">
            <ProtocolImage protocol={protocol} className="size-6" />
            <span className="text-base">{protocolDisplayName}</span>
          </div>
        )}
      </LabeledValue>
      {curator && (
        <LabeledValue label="Curator">
          <CuratorInfo curator={curator} size="base" className="text-base" />
        </LabeledValue>
      )}
      <LabeledValue label="Underlying token">
        <div className="flex items-center gap-1">
          <TokenImage symbol={asset.symbol} className="size-6" />
          <span className="text-base">{asset.symbol}</span>
        </div>
      </LabeledValue>
      {rewards.length > 0 && (
        <LabeledValue label="Reward tokens">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {rewards.map(({ asset }) => (
              <div className="flex items-center gap-1" key={asset.address}>
                <TokenImage symbol={asset.symbol} className="size-6" />
                <span className="text-base">{asset.symbol}</span>
              </div>
            ))}
          </div>
        </LabeledValue>
      )}
      {(explorerLink || protocol.protocolUrl) && (
        <LabeledValue label="External links">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {explorerLink && (
              <Button
                variant="default"
                size="default"
                className="flex items-center gap-1 rounded-lg text-base underline"
                asChild
              >
                <a href={`${explorerLink}/address/${vault.address}`} target="_blank" rel="noreferrer">
                  <span>{explorerName}</span>
                  <ExternalLinkIcon size={16} />
                </a>
              </Button>
            )}
            {protocol.protocolUrl && (
              <Button
                variant="default"
                size="default"
                className="flex items-center gap-1 rounded-lg text-base underline"
                asChild
              >
                <a href={protocol.protocolUrl} target="_blank" rel="noreferrer">
                  <span>{protocolDisplayName}</span> app
                  <ExternalLinkIcon size={16} />
                </a>
              </Button>
            )}
          </div>
        </LabeledValue>
      )}
    </div>
  )
}
